import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { toast } from 'sonner'
import {
  AlertTriangle, ArrowLeft, Check, ChevronRight, ExternalLink, FileText,
  Loader2, RefreshCw,
} from 'lucide-react'

import { api, ApiError } from '@/lib/api/client'
import type {
  InvoiceGenerationIssue, ProductInvoiceCreateResponse,
  ProductInvoiceDraft, ProductInvoicePreviewResponse,
} from '@/types/api'


// ── Utils ────────────────────────────────────────────────────────────────────


function fmtMoney(n: number, currency: string): string {
  return new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 2 }).format(n) + ' ' + currency
}

function thisMonthRange(): { start: string; end: string } {
  const now = new Date()
  const start = new Date(now.getFullYear(), now.getMonth(), 1)
  const end   = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  const fmt = (d: Date) => d.toISOString().slice(0, 10)
  return { start: fmt(start), end: fmt(end) }
}


// ── Page ─────────────────────────────────────────────────────────────────────


export default function InvoicePreviewPage() {
  // The page reads ?customerId=... from the route — TanStack Router would
  // typically pass this via route params, but to keep the route definition
  // small we accept it as a query string. Falls back to /subscriptions if absent.
  const search = useSearch({ strict: false }) as { customerId?: string }
  const customerId = search.customerId ? Number(search.customerId) : null
  const navigate = useNavigate()
  const qc = useQueryClient()

  const defaults = thisMonthRange()
  const [periodStart, setPeriodStart] = useState(defaults.start)
  const [periodEnd,   setPeriodEnd]   = useState(defaults.end)

  const previewQ = useQuery<ProductInvoicePreviewResponse, ApiError>({
    queryKey: ['product-invoice-preview', customerId, periodStart, periodEnd],
    queryFn:  () => api.get(
      `/customers/${customerId}/product-invoice-preview?periodStart=${periodStart}&periodEnd=${periodEnd}`,
    ),
    enabled:  customerId != null && !!periodStart && !!periodEnd,
    retry:    false,
  })

  // Server returns 422 with { issues: [...] } when pre-flight blocks; the
  // API client throws ApiError. We refetch the message body separately so the
  // issues list can render with deeplinks.
  const issues = useMemo<InvoiceGenerationIssue[]>(() => {
    if (!previewQ.error) return []
    try {
      // ApiError swallows the body; we keep the message and parse if it's JSON-shaped.
      const m = previewQ.error.message
      if (m.startsWith('{')) {
        const parsed = JSON.parse(m)
        if (Array.isArray(parsed.issues)) return parsed.issues
      }
    } catch { /* fall through */ }
    return []
  }, [previewQ.error])

  const createMut = useMutation<ProductInvoiceCreateResponse, ApiError>({
    mutationFn: async () => api.post(
      `/customers/${customerId}/product-invoice-drafts`,
      { periodStart, periodEnd },
    ),
    onSuccess: (res) => {
      toast.success(`Created ${res.created.length} draft invoice${res.created.length === 1 ? '' : 's'}`)
      qc.invalidateQueries({ queryKey: ['product-invoice-preview'] })
      qc.invalidateQueries({ queryKey: ['subscription-events'] })
      // Jump to the first new invoice in the time-invoicing module so the
      // user can review + push to Fortnox via the existing flow.
      if (res.created[0]) {
        navigate({ to: `/invoicing/composer?invoiceId=${res.created[0].invoiceId}` })
      }
    },
    onError: (e) => {
      // 422 with structured issues — surfaced via the issues list above.
      // Other failures show a toast.
      if (e instanceof ApiError && e.status !== 422) {
        toast.error(e.message)
      }
    },
  })

  if (customerId == null) {
    return (
      <div className="page">
        <div className="card" style={{ padding: 24, textAlign: 'center' }}>
          <div className="muted">No customer selected.</div>
          <Link to="/subscriptions" className="btn" style={{ marginTop: 12 }}>
            <ArrowLeft size={13} /> Back to subscriptions
          </Link>
        </div>
      </div>
    )
  }

  const drafts = previewQ.data?.drafts ?? []
  const allRows = drafts.flatMap(d => d.rows)
  const overall = drafts.reduce((acc, d) => ({
    gross:    acc.gross    + d.grossTotal,
    discount: acc.discount + d.discountTotal,
    net:      acc.net      + d.netTotal,
  }), { gross: 0, discount: 0, net: 0 })

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Invoicing · Product</div>
          <h1 className="page-title">Generate product invoice</h1>
          <div className="page-subtitle">
            Pre-flight check then create draft invoice(s) for product subscriptions in the chosen period.
            Lines, discounts, and backdated adjustments are computed from the same pure calculators
            the live preview uses — no math drift between preview and final draft.
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <Link to="/subscriptions" className="btn">
            <ArrowLeft size={13} /> Back
          </Link>
          <button className="btn" onClick={() => previewQ.refetch()}>
            <RefreshCw size={13} className={previewQ.isFetching ? 'spin' : undefined} /> Refresh
          </button>
        </div>
      </div>

      {/* Period picker */}
      <div className="card" style={{ padding: 14, marginBottom: 12, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Period start</div>
          <input type="date" className="input" value={periodStart} onChange={e => setPeriodStart(e.target.value)} />
        </div>
        <div>
          <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Period end</div>
          <input type="date" className="input" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} />
        </div>
        <div style={{ flex: 1 }} />
        <button
          className="btn btn-brand"
          disabled={drafts.length === 0 || createMut.isPending || issues.length > 0}
          onClick={() => createMut.mutate()}
        >
          {createMut.isPending ? <><Loader2 size={13} className="spin" /> Creating…</> : <><Check size={13} /> Create draft invoices</>}
        </button>
      </div>

      {/* Pre-flight issues block (422) */}
      {issues.length > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 12, borderLeft: '3px solid var(--ns-err)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <AlertTriangle size={16} style={{ color: 'var(--ns-err)' }} />
            <strong>Cannot generate yet — fix the {issues.length} issue{issues.length === 1 ? '' : 's'} below first.</strong>
          </div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {issues.map((i, idx) => (
              <li key={idx} style={{
                padding: '8px 0',
                borderTop: idx > 0 ? '1px solid var(--ns-border)' : 'none',
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <span className="mono muted" style={{ fontSize: 11 }}>{i.code}</span>
                <span style={{ flex: 1 }}>{i.message}</span>
                {i.fixUrl && (
                  <a href={i.fixUrl} className="btn btn-sm">
                    Fix <ExternalLink size={11} />
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Loading */}
      {previewQ.isLoading && (
        <div className="card" style={{ padding: 36, textAlign: 'center' }}>
          <Loader2 size={22} className="spin" style={{ margin: '0 auto 8px' }} />
          <div className="muted">Computing preview…</div>
        </div>
      )}

      {/* Empty */}
      {!previewQ.isLoading && drafts.length === 0 && issues.length === 0 && (
        <div className="card" style={{ padding: 36, textAlign: 'center' }}>
          <FileText size={28} className="muted" style={{ margin: '0 auto 8px' }} />
          <div style={{ fontWeight: 600, marginBottom: 4 }}>No billable activity in this period</div>
          <div className="muted" style={{ fontSize: 13 }}>
            No active subscription lines overlap {periodStart} → {periodEnd}, or every line is on trial.
          </div>
        </div>
      )}

      {/* Drafts */}
      {drafts.length > 0 && (
        <>
          {/* Summary across all drafts */}
          <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', marginBottom: 12 }}>
            <SummaryCard label="Drafts to create" value={String(drafts.length)} accent="info" />
            <SummaryCard label="Total gross"      value={fmtMoney(overall.gross,    drafts[0].currency)} accent="info" />
            <SummaryCard label="Total discount"   value={`−${fmtMoney(overall.discount, drafts[0].currency)}`} accent="ok" />
            <SummaryCard label="Net to invoice"   value={fmtMoney(overall.net,      drafts[0].currency)} accent="ok" />
          </div>

          {drafts.map((draft, idx) => <DraftCard key={idx} draft={draft} />)}

          <div className="muted" style={{ fontSize: 12, marginTop: 8, textAlign: 'right' }}>
            {allRows.length} row{allRows.length === 1 ? '' : 's'} across {drafts.length} draft{drafts.length === 1 ? '' : 's'}
          </div>
        </>
      )}
    </div>
  )
}


function SummaryCard({ label, value, accent }: { label: string; value: string; accent: 'ok' | 'info' | 'warn' }) {
  const color = {
    ok:   'var(--ns-ok)',
    info: 'var(--ns-brand-strong)',
    warn: 'var(--ns-warn)',
  }[accent]
  return (
    <div className="stat" style={{ borderLeft: `3px solid ${color}` }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value tabular">{value}</div>
    </div>
  )
}


function DraftCard({ draft }: { draft: ProductInvoiceDraft }) {
  const subscriptionRows = draft.rows.filter(r => !r.isDiscountRow)
  const discountRows     = draft.rows.filter(r => r.isDiscountRow)
  const adjustmentCount  = subscriptionRows.filter(r => r.adjustmentPeriodStart != null).length
  const trialRowCount    = subscriptionRows.filter(r => r.trialDays > 0).length

  return (
    <div className="card" style={{ padding: 0, marginBottom: 14, overflow: 'hidden' }}>
      <div style={{
        padding: '14px 18px',
        borderBottom: '1px solid var(--ns-border)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>
            {draft.productType ? `${draft.productType} invoice` : 'Bundled invoice'}
            <span className="muted" style={{ fontSize: 12, marginLeft: 10, fontWeight: 500 }}>
              {draft.periodStart} → {draft.periodEnd}
            </span>
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            {subscriptionRows.length} subscription row{subscriptionRows.length === 1 ? '' : 's'}
            {adjustmentCount > 0 && ` · ${adjustmentCount} backdated`}
            {trialRowCount > 0   && ` · ${trialRowCount} trial`}
            {discountRows.length > 0 && ` · ${discountRows.length} discount row${discountRows.length === 1 ? '' : 's'}`}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="muted" style={{ fontSize: 11.5 }}>Net</div>
          <div className="tabular" style={{ fontWeight: 700, fontSize: 16 }}>
            {fmtMoney(draft.netTotal, draft.currency)}
          </div>
        </div>
      </div>

      <table className="table">
        <thead>
          <tr>
            <th>Description</th>
            <th style={{ textAlign: 'right' }}>Qty</th>
            <th style={{ textAlign: 'right' }}>Unit price</th>
            <th style={{ textAlign: 'right' }}>Amount</th>
            <th style={{ textAlign: 'right' }}>Account #</th>
          </tr>
        </thead>
        <tbody>
          {draft.rows.map((r, idx) => (
            <tr key={idx} style={{ background: r.isDiscountRow ? 'var(--ns-surface-2)' : undefined }}>
              <td>
                {r.description}
                {r.adjustmentPeriodStart && (
                  <span className="mono muted" style={{ fontSize: 11, marginLeft: 8 }}>
                    [{r.adjustmentPeriodStart} → {r.adjustmentPeriodEnd}]
                  </span>
                )}
              </td>
              <td className="tabular" style={{ textAlign: 'right', fontSize: 12.5 }}>
                {r.isDiscountRow ? '—' : r.quantity.toFixed(4)}
              </td>
              <td className="tabular" style={{ textAlign: 'right' }}>
                {fmtMoney(r.unitPrice, draft.currency)}
              </td>
              <td className="tabular" style={{
                textAlign: 'right', fontWeight: 600,
                color: r.isDiscountRow ? 'var(--ns-ok)' : undefined,
              }}>
                {r.isDiscountRow ? '−' : ''}{fmtMoney(Math.abs(r.amount), draft.currency)}
              </td>
              <td className="mono" style={{ textAlign: 'right', fontSize: 12, color: 'var(--ns-text-muted)' }}>
                {r.fortnoxAccountNumber}
              </td>
            </tr>
          ))}
          <tr style={{ background: 'var(--ns-surface-2)', fontWeight: 700 }}>
            <td colSpan={3} style={{ textAlign: 'right' }}>Gross</td>
            <td className="tabular" style={{ textAlign: 'right' }}>{fmtMoney(draft.grossTotal, draft.currency)}</td>
            <td />
          </tr>
          {draft.discountTotal !== 0 && (
            <tr>
              <td colSpan={3} style={{ textAlign: 'right' }} className="muted">Discount</td>
              <td className="tabular" style={{ textAlign: 'right', color: 'var(--ns-ok)' }}>
                −{fmtMoney(draft.discountTotal, draft.currency)}
              </td>
              <td />
            </tr>
          )}
          <tr style={{ fontWeight: 700, background: 'color-mix(in oklch, var(--ns-brand-strong) 8%, var(--ns-surface))' }}>
            <td colSpan={3} style={{ textAlign: 'right' }}>Net <ChevronRight size={11} /></td>
            <td className="tabular" style={{ textAlign: 'right' }}>{fmtMoney(draft.netTotal, draft.currency)}</td>
            <td />
          </tr>
        </tbody>
      </table>
    </div>
  )
}
